import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button, CardSubtitle, CardTitle } from 'reactstrap'
import { Divider, Popconfirm, message } from 'antd'
import { Spinner } from 'react-bootstrap'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import { notifyServiceResult } from '../services'
import { handleError } from '../../../helpers'

export default function ServiceResultProfileTopbar({ serviceResult }) {
	const currentUser = useSelector(state => state.auth.user)
	const [loading, setLoading] = useState(false)

	if(!serviceResult) return null

	const captureElement = (element) => {
		const restored = []
		let parent = element.parentElement
		while (parent) {
			restored.push({
				el: parent,
				overflow: parent.style.overflow,
				overflowX: parent.style.overflowX,
				overflowY: parent.style.overflowY,
			})
			parent.style.overflow = 'visible'
			parent.style.overflowX = 'visible'
			parent.style.overflowY = 'visible'
			parent = parent.parentElement
		}

		return html2canvas(element, {
			useCORS: true,
			allowTaint: false,
			imageTimeout: 60000,
			scale: 3,
			backgroundColor: '#ffffff',
			width: element.scrollWidth,
			height: element.scrollHeight,
		}).finally(() => {
			restored.forEach(({ el, overflow, overflowX, overflowY }) => {
				el.style.overflow = overflow
				el.style.overflowX = overflowX
				el.style.overflowY = overflowY
			})
		})
	}

	const addCanvasToPdf = (pdf, canvas, { startNewPage = false, fitToPage = false } = {}) => {
		const imgData = canvas.toDataURL('image/png')
		const pageHeight = pdf.internal.pageSize.getHeight()
		const pageWidth = pdf.internal.pageSize.getWidth()

		if (fitToPage) {
			const margin = 4
			const maxWidth = pageWidth - margin * 2
			const maxHeight = pageHeight - margin * 2
			const scale = Math.min(maxWidth / canvas.width, maxHeight / canvas.height)
			const imgWidth = canvas.width * scale
			const imgHeight = canvas.height * scale
			const xOffset = (pageWidth - imgWidth) / 2
			const yOffset = (pageHeight - imgHeight) / 2

			if (startNewPage) pdf.addPage('a4')
			pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight)
			return
		}

		const margin = 8
		const imgWidth = pageWidth - margin * 2
		const imgHeight = canvas.height * imgWidth / canvas.width

		if (startNewPage) pdf.addPage('a4')

		if (imgHeight <= pageHeight - margin * 2) {
			pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight)
			return
		}

		let position = margin
		pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)

		while (position + imgHeight > pageHeight + 0.5) {
			position -= pageHeight
			pdf.addPage('a4')
			pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
		}
	}

	const printDocument = () => {
		setLoading(true)

		const reportInput = document.getElementById('serviceReportPreviewContainer')
		const resultInput = document.getElementById('reportPreviewContainer')
		const elements = [reportInput, resultInput].filter(Boolean)

		Promise.all(elements.map(captureElement))
			.then((canvases) => {
				const pdf = new jsPDF('p', 'mm', 'a4', true)
				canvases.forEach((canvas, index) => addCanvasToPdf(pdf, canvas, {
					startNewPage: index > 0,
					fitToPage: Boolean(reportInput) && index === 0,
				}))
				pdf.save(`Informe de Servicio #${serviceResult.id}.pdf`)
			})
			.finally(() => setLoading(false))
	}

	const handleSendServiceResult = () => {
		notifyServiceResult(serviceResult.id)
			.then(res => message.success(res.data.message))
			.catch(error => handleError(error))
	}

	return (
		<React.Fragment>
			<div className='float-right pt-5'>				
				<Link to={`/service_reports/${serviceResult.service_report.id}`}>
					<Button color='primary' outline className='ml-7'>
						Reporte de Servicio Asociado
					</Button>
				</Link>
				<Divider type="vertical" />
				<Link to={`/service_results/preview/${serviceResult.id}`}>
					<Button color='primary' outline className='ml-7'>
						Imprimir
					</Button>
				</Link>
				<Divider type="vertical" />
				<Button 
					color='primary' 
					outline 
					onClick={printDocument}
					disabled={loading}
					// style={{display:'none'}}
				>
					{ loading && <Spinner animation='border' size="sm" className='mr-1' /> }
					Exportar PDF
				</Button>
				{ currentUser.role === 'admin' && (
					<React.Fragment>
						<Divider type="vertical" />
						<Popconfirm
							title="Esta acción no puede revertirse"
							description="El email se enviara a todos los empleados asociados a la sede"
							okText="Enviar Informe"
							cancelText="Cancelar"
							onConfirm={handleSendServiceResult}
						>
							<span>
								<Button color="primary" outline>
									Enviar Informe
								</Button>
							</span>
						</Popconfirm>
					</React.Fragment>
				)}
			</div>

			<CardTitle>
				{serviceResult.result_format.name} #{serviceResult.sku}
			</CardTitle>
			<CardSubtitle>
			{serviceResult.service_report.service.location.company.legal_name} - {serviceResult.service_report.service.name}
			</CardSubtitle>
		</React.Fragment>
	)
}